import { ApplicationConfig, ENVIRONMENT_INITIALIZER, inject, LOCALE_ID, provideZoneChangeDetection, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors } from '@angular/common/http';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';

// NgRx Store
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { reducers } from '../../../shared/src/lib/store/app.states';
import { AuthEffects } from '../../../shared/src/lib/store/effects/auth.effects';
import { UserEffects } from '../../../shared/src/lib/store/effects/user.effects';
import { LilateEffects } from '../../../shared/src/lib/store/effects/lilate.effects';
import { ElateEffects } from '../../../shared/src/lib/store/effects/elate.effects';
import { ExerciseEffects } from '../../../shared/src/lib/store/effects/exercise.effects';
import { ExaminationEffects } from '../../../shared/src/lib/store/effects/examination.effects';
import { SlideTextEffects } from '../../../shared/src/lib/store/effects/slide-text.effects';
import { WebSocketEffects } from '../../../shared/src/lib/store/effects/websockets.effects';
import { ChartEffects } from '../../../shared/src/lib/store/effects/chart.effects';
import { FormationEffects } from '../../../shared/src/lib/store/effects/formation.effects';
import { GroupEffects } from '../../../shared/src/lib/store/effects/group.effects';
import { RoutePermissionEffects } from '../../../shared/src/lib/store/effects/routePermission.effects';
import { clearState } from '../../../shared/src/lib/store/meta-reducers/clear-state.meta-reducer';

// Fuse
import { provideFuse } from '../../../shared/src/lib/@fuse/fuse.provider';
import { provideIcons } from '../../../shared/src/lib/icons/icons.provider';

// Interceptors
import { authProviderInterceptor } from '../../../shared/src/lib/interceptors/authProvider.interceptor';

// Services
import { AuthenticationService } from '../../../shared/src/lib/service/authentification.service';
import { FormationService } from '../../../shared/src/lib/service/formation.service';
import { StudentService } from '../../../shared/src/lib/service/student.service';
import { TeacherService } from '../../../shared/src/lib/service/teacher.service';
import { OccurrenceService } from '../../../shared/src/lib/service/occurrence.service';
import { EventService } from '../../../shared/src/lib/service/event.service';
import { MessageService } from '../../../shared/src/lib/service/message.service';
import { DocumentService } from '../../../shared/src/lib/service/document.service';
import { ExerciceService } from '../../../shared/src/lib/service/exercice.service';
import { ExerciceSessionService } from '../../../shared/src/lib/service/exerciceSession.service';
import { ExerciceSheetService } from '../../../shared/src/lib/service/exerciceSheet.service';
import { PollService } from '../../../shared/src/lib/service/poll.service';
import { PollResponseService } from '../../../shared/src/lib/service/pollResponse.service';
import { LanguageService } from '../../../shared/src/lib/service/language.service';
import { LilateService } from '../../../shared/src/lib/service/lilate.service';
import { PlanService } from '../../../shared/src/lib/service/plan.service';
import { PlanServiceNew } from '../../../shared/src/lib/service/plan.service.new';
import { MaterialService } from '../../../shared/src/lib/service/material.service';
import { KiwixiGlobals } from '../../../shared/src/lib/kiwixi/kiwixi.globals';
import { SpeechService } from '../../../shared/src/lib/service/speech.service';
import { ProfilePhotosService } from '../../../shared/src/lib/service/profilePhotos.service';

// Guards
import { AuthFuseGuard } from '../../../shared/src/lib/guard/authFuse.guard';
import { NoAuthFuseGuard } from '../../../shared/src/lib/guard/noAuthFuse.guard';
import { MaintenanceGuard } from '../../../shared/src/lib/guard/maintenance.guard';

// Routes
import { appRoutes } from './student.routes';
import { GroupService } from '../../../shared/src/lib/service/group.service';
import { RoutePermissionService } from '../../../shared/src/lib/service/routePermission.service';
import { ConversationService } from '../../../shared/src/lib/service/conversation.service';
import { KiwixiService } from '../../../shared/src/lib/kiwixi/kiwixi.service';
import { InitAppService } from '../../../shared/src/lib/service/initapp.service';
import { ScrollToService } from '../../../shared/src/lib/service/scrollto.service';
import { TranslateService, TranslateStore, TranslateModule } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { DownloadLingueoService } from '../../../shared/src/lib/directive/download-lingueo/download-lingueo.service';
import { RadarService } from '../../../shared/src/lib/feature/radar2/radar2.service';
import { AchievementService } from '../../../shared/src/lib/service/achievement.service';
import { AchievementDoneservice } from '../../../shared/src/lib/service/achievementDone.service';
import { AchievementManagerService } from '../../../shared/src/lib/service/achievementManager.service';
import { DictionnaryService } from '../../../shared/src/lib/service/dictionnary.service';
import { ExaminationService } from '../../../shared/src/lib/service/examination.service';
import { ExerciceAnswerChoiceService } from '../../../shared/src/lib/service/exerciceAnswerChoice.service';
import { ExerciceCategoryService } from '../../../shared/src/lib/service/exerciceCategory.service';
import { ExerciceMediaService } from '../../../shared/src/lib/service/exerciceMedia.service';
import { ExerciceQuestionService } from '../../../shared/src/lib/service/exerciceQuestion.service';
import { ExerciceResponseService } from '../../../shared/src/lib/service/exerciceResponse.service';
import { ExerciceSpecificContentService } from '../../../shared/src/lib/service/exerciceSpecificContent.service';
import { HelpNotifService } from '../../../shared/src/lib/service/helpNotif.service';
import { RateStudentService } from '../../../shared/src/lib/service/rate-student.service';
import { RatingTeachersService } from '../../../shared/src/lib/service/rating-teachers.service';
import { TaskService } from '../../../shared/src/lib/service/task.service';
import { sentryProviders } from '../../../shared/src/lib/sentry/sentry.config';
import { StateObservable, Store } from '@ngrx/store';
import { YouTubePlayerModule } from '@angular/youtube-player';
import { AiExamService } from '../../../shared/src/lib/service/aiExam.service';
import { CompanyService } from '../../../shared/src/lib/service/company.service';
import { ExaminationSessionAttendeeLogsConnexionService } from '../../../shared/src/lib/service/examination-session-attendee.service';
import { ExaminationSessionService } from '../../../shared/src/lib/service/examination-session.service';
import { ExportsService } from '../../../shared/src/lib/service/export.service';
import { FiltersService } from '../../../shared/src/lib/service/filters.service';
import { GroupFormationsService } from '../../../shared/src/lib/service/groupFormations.service';
import { NotificationService } from '../../../shared/src/lib/service/notification.service';
import { PathEventService } from '../../../shared/src/lib/service/pathEvent.service';
import { SessionService } from '../../../shared/src/lib/service/session.service';
import { TestingService } from '../../../shared/src/lib/service/testing.service';
import { UserService } from '../../../shared/src/lib/service/user.service';
import { WebsocketService } from '../../../shared/src/lib/service/websocket.service';
import { ExaminationSessionSlideService } from '../../../shared/src/lib/service/examination-session-slide.service';
import { ExaminationSlidesTextService } from '../../../shared/src/lib/service/examination-slide-text.service';
import { ExaminationVariantService } from '../../../shared/src/lib/service/examination-variant.service';
import { ExaminationSlidesService } from '../../../shared/src/lib/service/examination-slides.service';
import { OnlineMeetingsService } from '../../../shared/src/lib/service/onlineMeetings.service';
import { CertificationService } from '../../../shared/src/lib/service/certification.service';
import { CorporateService } from '../../../shared/src/lib/service/corporate.service';
import { PartnersService } from '../../../shared/src/lib/service/partners.service';
import { UpcomingService } from '../../../shared/src/lib/service/upcoming.service';
import { TimezoneService } from '../../../shared/src/lib/service/timezone.service';
import { customLoadingInterceptor } from '../../../shared/src/lib/interceptors/loading.interceptor';
import { fuseLoadingInterceptor } from '@fuse/services/loading';

export const studentConfig: ApplicationConfig = {
  providers: [
    // Zone change detection optimisé
    provideZoneChangeDetection({ eventCoalescing: true }),

    // Animations Material
    provideAnimations(),

    // Router avec routes lazy-loaded
    provideRouter(appRoutes),

    // HTTP Client avec intercepteurs
    provideHttpClient(
      withInterceptors([
        authProviderInterceptor,
        customLoadingInterceptor
      ])
    ),

    // NgRx Store et Effects
    importProvidersFrom(
      StoreModule.forRoot(reducers, {
        metaReducers: [clearState],  // ✅ Meta-reducer pour reset au logout
      }),
      EffectsModule.forRoot([
        AuthEffects,
        UserEffects,
        LilateEffects,
        ElateEffects,
        ExerciseEffects,
        ExaminationEffects,
        SlideTextEffects,
        WebSocketEffects,
        ChartEffects,
        FormationEffects,
        GroupEffects,
        RoutePermissionEffects
      ]),
      TranslateModule.forRoot()
    ),

    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'D',
        },
        display: {
          dateInput: 'DDD',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'DD',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },
    { provide: MAT_DATE_LOCALE, useValue: 'fr' },
    { provide: LOCALE_ID, useValue: 'fr-FR' },

    // Authentication Service Initializer
    {
      provide: ENVIRONMENT_INITIALIZER,
      useValue: () => inject(AuthenticationService),
      multi: true,
    },

    // Fuse Framework
    provideIcons(),
    provideFuse({
      fuse: {
        layout: 'classy',
        scheme: 'light',
        theme: 'theme-brand',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-brand',
            name: 'Brand',
          },
          {
            id: 'theme-teal',
            name: 'Teal',
          },
          {
            id: 'theme-rose',
            name: 'Rose',
          },
          {
            id: 'theme-purple',
            name: 'Purple',
          },
          {
            id: 'theme-amber',
            name: 'Amber',
          },
        ],
      },
    }),

    // Services métier
    AuthenticationService,
    FormationService,
    StudentService,
    TeacherService,
    OccurrenceService,
    EventService,
    MessageService,
    DocumentService,
    ExerciceService,
    ExerciceSessionService,
    ExerciceSheetService,
    PollService,
    PollResponseService,
    LanguageService,
    LilateService,
    PlanService,
    PlanServiceNew,
    MaterialService,
    KiwixiGlobals,
    SpeechService,
    ProfilePhotosService,
    GroupService,
    RoutePermissionService,
    ConversationService,

    KiwixiService,
    InitAppService,
    ScrollToService,
    TranslateService,
    TranslateStore,
    DictionnaryService,
    RatingTeachersService,
    RateStudentService,
    DownloadLingueoService,
    ExerciceSpecificContentService,
    ExerciceCategoryService,
    ExerciceQuestionService,
    ExerciceResponseService,
    ExerciceAnswerChoiceService,
    ExerciceMediaService,
    TaskService,
    AchievementService,
    AchievementDoneservice,
    AchievementManagerService,
    HelpNotifService,
    DeviceDetectorService,
    ExaminationService,
    RadarService,
    DatePipe,
    sentryProviders,

    FiltersService,
    ExaminationSessionService,
    ExaminationSessionSlideService,
    ExaminationSlidesTextService,
    ExaminationSlidesService,
    ExaminationVariantService,
    WebsocketService,
    ExerciceMediaService,
    YouTubePlayerModule,
    UserService,
    ExaminationSessionAttendeeLogsConnexionService,
    PathEventService,
    TestingService,
    FormationService,
    OccurrenceService,
    SessionService,
    ScrollToService,
    ExportsService,
    OnlineMeetingsService,
    CompanyService,
    CertificationService,
    LanguageService,
    CorporateService,
    GroupFormationsService,
    PartnersService,
    DatePipe,
    NotificationService,
    AiExamService,
    UpcomingService,
    TimezoneService,
    // Guards
    AuthFuseGuard,
    NoAuthFuseGuard,
    MaintenanceGuard,
  ],
};
